import React, { useState } from 'react';
import { useAuth } from 'context/authContext';
import { useNavigate } from 'react-router-dom';
import { ForgotPassword, FormInput, OrangeButton, SmallButton } from 'components';
import axios from 'axios';
import { Form } from 'antd';
import Logo from 'assets/images/logo.svg';
import LogoClean from 'assets/images/pf25-logo-clean.png';
import classNames from 'classnames';
import { endpoints } from 'api/endpoints';
import { toast } from 'react-toastify';
import { ROLE_TYPES } from 'api/constants';

export const ClientLoginForm = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [isForgetPassword, setIsForgetPassword] = useState(false);

  const onFinish = (values) => {
    axios
      .post(endpoints.clientLogin, values)
      .then(async (response) => {
        if (response.status === 200) {
          const userRole = response.data.user.role;
          const token = response.data.token;

          login(token, userRole);
          navigate('/dashboard');
        }
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || 'Login failed');
      });
  };

  const onValuesChange = (_, allValues) => {
    const email = allValues.email;
    const password = allValues.password;
    setBtnDisabled(
      !(email !== undefined && email !== '' && password !== undefined && password !== ''),
    );
  };

  if (isForgetPassword) {
    return <ForgotPassword domain={ROLE_TYPES.client} showLogin={() => setIsForgetPassword(false)} isMobile={isMobile} />;
  }

  // DESKTOP VIEW LOGIN FORM
  if (!isMobile) {
    return (
      <div className="flex flex-col bg-white rounded-[60px] pt-10 pb-12 w-[40rem] h-[calc(100%-64px)] min-h-[46rem] my-8 shadow-block-shadow">
        <img className="w-48 mb-6 self-center object-contain" src={Logo} alt="pw25 logo" />
        <div
          className={classNames(
            'flex-1 relative w-full flex flex-col py-8 border-b border-t border-color-divider px-20 self-center items-center',
            'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            'after:content-[""] after:absolute after:w-4 after:h-4 after:rounded-full after:border after:border-color-divider after:bg-white after:z-10 after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2',
          )}
        >
          <p className="mb-4 nunito-title text-22">Welcome</p>
          <p className="text-lightBlue text-base leading-[24px]">Please login into your account</p>
          <Form
            className="mt-16 flex flex-col w-full gap-y-6"
            name="auth"
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            autoComplete="off"
            requiredMark={'optional'}
            clearOnDestroy={false}
          >
            <FormInput
              label="Login"
              name="email"
              placeholder="Email"
              required
              errorMessage="Please enter valid email"
            />
            <FormInput
              type="password"
              label="Password"
              name="password"
              placeholder="Password"
              required
              errorMessage="Please enter correct password"
            />
            <OrangeButton
              type="submit"
              className="self-center mt-2"
              btnClassName="ant-btn-green border-none shadow-none font-semibold text-15 !h-[52px] rounded-xl px-12"
              label="Login"
              disabled={btnDisabled}
            />
          </Form>
        </div>
        <SmallButton
          className="self-center !mt-7"
          label="Forgot password? "
          onClick={() => setIsForgetPassword(true)}
        />
      </div>
    );
  }

  // MOBILE VIEW LOGIN FORM
  return (
    <div className="flex flex-col bg-white rounded-3xl pt-8 pb-8 w-full max-w-[390px] shadow-block-shadow px-4 my-4">
      <img className="w-36 mb-4 self-center object-contain" src={LogoClean} alt="pw25 logo" />
      <div
        className={classNames(
          'flex-1 relative w-full flex flex-col py-6 border-b border-t border-color-divider px-4 self-center items-center',
          'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
          'after:content-[""] after:absolute after:w-4 after:h-4 after:rounded-full after:border after:border-color-divider after:bg-white after:z-10 after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2',
        )}
      >
        <p className="mb-2 nunito-title text-20">Welcome</p>
        <p className="text-lightBlue text-14 leading-[24px]">Please login into your account</p>
        <Form
          className="mt-8 flex flex-col w-full gap-y-5"
          name="auth"
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          autoComplete="off"
          requiredMark={'optional'}
          clearOnDestroy={false}
        >
          <FormInput
            label="Login"
            name="email"
            placeholder="Email"
            required
            errorMessage="Please enter valid email"
          />
          <FormInput
            type="password"
            label="Password"
            name="password"
            placeholder="Password"
            required
            errorMessage="Please enter correct password"
          />
          <OrangeButton
            type="submit"
            className="w-full mt-2"
            btnClassName="ant-btn-green border-none shadow-none font-semibold text-15 !h-12 rounded-xl w-full"
            label="Login"
            disabled={btnDisabled}
          />
        </Form>
      </div>
      <SmallButton
        className="self-center !mt-5"
        label="Forgot password?"
        onClick={() => setIsForgetPassword(true)}
      />
    </div>
  );
};
